import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function PaginationBar({ page, totalPages, total, perPage, onPage }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="pagination" data-testid="csp-pagination">
      <span className="pagination-info">
        {((page - 1) * perPage + 1).toLocaleString()}–{Math.min(page * perPage, total).toLocaleString()} of {total.toLocaleString()}
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

function FlagCell({ value }) {
  if (value) {
    return <span className="badge badge-green" style={{ fontSize: 11, fontWeight: 700 }}>✓</span>;
  }
  return <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>;
}

export default function CspResellersPage() {
  const navigate = useNavigate();
  const [data,       setData]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [applied,    setApplied]    = useState('');

  const fetchData = useCallback(async (p, q) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: 50 };
      if (q) params.q = q;
      const r = await axios.get(`${API}/api/partners/csp-resellers`, { params, withCredentials: true });
      setData(r.data.data);
      setTotal(r.data.total);
      setTotalPages(r.data.total_pages);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(1, ''); }, []); // eslint-disable-line

  const handleSearch = () => { setApplied(search); fetchData(1, search); };
  const handleReset  = () => { setSearch(''); setApplied(''); fetchData(1, ''); };

  return (
    <div data-testid="csp-resellers-page">
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        Dashboard &gt;{' '}
        <span style={{ cursor: 'pointer', color: '#3182ce' }} onClick={() => navigate('/partners')} data-testid="csp-back-link">
          Partner Dashboard
        </span>{' '}
        &gt; CSP Resellers
      </div>

      <div className="page-header">
        <h1>CSP Resellers</h1>
        <p>Reseller domains managed by CSP partners</p>
      </div>

      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group" style={{ flex: 3 }}>
          <label className="filter-label">Search domain</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              className="filter-input"
              style={{ paddingLeft: 30 }}
              placeholder="Search by domain…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              data-testid="csp-search-input"
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary"   onClick={handleSearch} data-testid="csp-search-btn">Search</button>
          <button className="btn btn-secondary" onClick={handleReset}  data-testid="csp-reset-btn">Reset</button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header">
          <div>
            <span className="table-title" style={{ fontFamily: 'monospace' }}>csp_resellers</span>
            <span className="table-count" style={{ marginLeft: 8 }}>({total.toLocaleString()} rows)</span>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : data.length === 0 ? (
          <div className="empty-state">
            <h3>No rows found</h3>
            <p>{applied ? 'Try a different search term' : 'Table appears to be empty'}</p>
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table data-testid="csp-table">
                <thead>
                  <tr>
                    <th>DOMAIN</th>
                    <th>CSP PARTNER ID</th>
                    <th>ALLOWED SEATS</th>
                    <th style={{ textAlign: 'center' }}>ESI</th>
                    <th style={{ textAlign: 'center' }}>MAICPP</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => {
                    const bothFalse = !row.is_esi && !row.is_maicpp;
                    return (
                      <tr
                        key={i}
                        data-testid={`csp-row-${i}`}
                        style={bothFalse ? { background: '#fffbeb' } : undefined}
                      >
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {row.domain}
                          {bothFalse && (
                            <AlertTriangle size={12} style={{ marginLeft: 6, color: '#f59e0b', verticalAlign: 'middle' }} />
                          )}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.csp_po_id ?? '—'}</td>
                        <td>{row.allowed_seats ?? '—'}</td>
                        <td style={{ textAlign: 'center' }}><FlagCell value={row.is_esi} /></td>
                        <td style={{ textAlign: 'center' }}><FlagCell value={row.is_maicpp} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={totalPages} total={total} perPage={50} onPage={p => fetchData(p, applied)} />
          </>
        )}
      </div>
    </div>
  );
}
